import React, { forwardRef } from 'react';
import { DATA_GRID_LOADING_INDICATOR_HEIGHT_WIDTH } from 'components/common/DataGridDevEx/DataGrid.constants';
import 'devextreme/dist/css/dx.common.css';
import 'devextreme/dist/css/dx.light.css';
import DataGrid, { Column, Scrolling, GroupPanel, SearchPanel, LoadPanel, HeaderFilter, Editing, Button, RowDragging } from 'devextreme-react/data-grid';
import { IDisplayFieldsGridProps, IDisplayFieldGridsHandle } from './DisplayFieldsGrid.properties';
import { withStyles } from '@material-ui/core';
import styles from './DisplayFieldsGrid.styles';
import { ACTION_COLUMN_WIDTH, DRAG_COLUMN_WIDTH, FIELD_COLUMN_WIDTH } from '../WidgetConfigurationContent.constants';
import { useFetchDashboardWidgetFieldList, useSetDashboardWidgetFieldListQueryData } from 'api/dashboardMaintenanceEnquiry/dashboardMaintenanceEnquiry';

const DisplayFieldsGrid = (props: IDisplayFieldsGridProps, ref: React.Ref<IDisplayFieldGridsHandle>) => {
    const { classes, setDisplayFieldsDataState, widgetId } = props;
    const { data: dashboardWidgetFieldListData, refetch: refetchDashboardQuickLinks } = useFetchDashboardWidgetFieldList(widgetId);
    const setDashboardWidgetFieldListQueryData = useSetDashboardWidgetFieldListQueryData();
    const dataGridInnerRef = React.useRef<DataGrid>(null) as React.MutableRefObject<DataGrid | null>;

    React.useEffect(
        () => {
            if (setDisplayFieldsDataState) {
                setDisplayFieldsDataState(dashboardWidgetFieldListData);
            }
        },
        [dashboardWidgetFieldListData]
    );

    React.useImperativeHandle(ref, () => ({
        refetch: () => {
            refetchDashboardQuickLinks();
        },
        getData: () => {
            if (dataGridInnerRef.current) {

                return dataGridInnerRef.current?.instance?.getDataSource()?.items();
            }

            return [];
        }
    }));

    const onDelete = React.useCallback(
        async (e: any): Promise<void> => {
            const rowKey = e?.row?.key;
            if (rowKey) {
                const { WidgetFieldId } = rowKey;

                const filteredFieldList = dashboardWidgetFieldListData?.DashboardWidgetFields?.filter(
                    (item) => item.WidgetFieldId !== WidgetFieldId
                );

                const updatedFieldList = filteredFieldList.map((item, index) => ({
                    ...item,
                    Sequence: index + 1
                }));

                setDashboardWidgetFieldListQueryData(Number(widgetId), {
                    DashboardWidgetFields: updatedFieldList
                });
            }
        },
        [widgetId, dashboardWidgetFieldListData, setDashboardWidgetFieldListQueryData]
    );

    return (
        <DataGrid
            className={classes.DisplayFieldsGrid}
            dataSource={dashboardWidgetFieldListData?.DashboardWidgetFields}
            ref={dataGridInnerRef}
            showBorders={true}
            renderAsync={true}
            remoteOperations={true}
            allowColumnResizing={true}
            columnResizingMode={'nextColumn'}
            hoverStateEnabled={true}
            noDataText=''
            columnAutoWidth={true}
            disabled={false}
        >
            <Editing
                refreshMode={'reshape'}
                mode='row'
                allowAdding={false}
                allowDeleting={true}
                allowUpdating={true}
            />
            <Column
                type='drag'
                width={DRAG_COLUMN_WIDTH}
                fixed={true}
            />
            <HeaderFilter visible />
            <Column
                dataField={'Label'}
                caption={'Selected Fields'}
                allowHeaderFiltering={false}
                minWidth={FIELD_COLUMN_WIDTH}
                allowSorting={false}
            >
            </Column>
            <Column type={'buttons'} width={ACTION_COLUMN_WIDTH} allowResizing={false} fixed={true}>
                <Button name={'delete'} icon={'trash'} onClick={onDelete} />
            </Column>
            <RowDragging
                allowReordering={true}
                onReorder={async (e) => {
                    const visibleRows = e.component.getVisibleRows();
                    const reordered = [...dashboardWidgetFieldListData?.DashboardWidgetFields];

                    const fromIndex = reordered.indexOf(e.itemData);
                    const toIndex = reordered.indexOf(visibleRows[e.toIndex].data);

                    reordered.splice(fromIndex, 1);
                    reordered.splice(toIndex, 0, e.itemData);

                    const updatedFieldList = reordered.map((item, index) => ({
                        ...item,
                        Sequence: index + 1,
                    }));

                    setDashboardWidgetFieldListQueryData(Number(widgetId), {
                        DashboardWidgetFields: updatedFieldList,
                    });
                }}
                showDragIcons={true}
            />
            <GroupPanel visible={false} />
            <SearchPanel visible={false} />
            <Scrolling mode={'infinite'} />
            <LoadPanel shading={false} height={DATA_GRID_LOADING_INDICATOR_HEIGHT_WIDTH} width={DATA_GRID_LOADING_INDICATOR_HEIGHT_WIDTH} text={''} showPane={false} />
        </DataGrid>
    );
};

export default withStyles(styles, { index: 1 })(forwardRef(DisplayFieldsGrid));
